import { usePunditUserContext } from "@circle-react/contexts";
import { useIsMemberToMemberMessagingEnabled } from "@circle-react/hooks/useIsMessagingEnabled";
import type { Member } from "@circle-react/types/Chat/Member";
import { isObject } from "lodash";

// Note:
// This hook can still return true if member messaging is disabled and
// they do not have an existing chat room with the admin.
export const useCanMessageMember = ({
  shouldHideMessageButton = false,
}: {
  shouldHideMessageButton?: boolean;
} = {}) => {
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const isMemberToMemberMessagingEnabled =
    useIsMemberToMemberMessagingEnabled();

  const shouldShowMessageOption = (member: Member) => {
    if (!isObject(member)) {
      return false;
    }

    const isSenderAdmin = currentCommunityMember?.is_admin;
    const isReceiverAdmin = member.admin;
    const isReceiverExists = member.profile_confirmed_at && !member.is_deleted;
    const isCurrentCommunityMemberProfile =
      currentCommunityMember?.id == member.id;

    const isCommunityMessagingDisabled = !currentCommunity?.messaging_enabled;

    if (shouldHideMessageButton) {
      return false;
    }

    if (
      !isReceiverExists ||
      isCurrentCommunityMemberProfile ||
      isCommunityMessagingDisabled
    ) {
      return false;
    }

    if (
      isReceiverAdmin &&
      !(member.messaging_enabled_by_member && member.messaging_enabled_by_admin)
    ) {
      return false;
    }

    if (isSenderAdmin) {
      return true;
    }

    if (
      !isReceiverAdmin &&
      !isSenderAdmin &&
      !isMemberToMemberMessagingEnabled
    ) {
      return false;
    }

    return (
      member.messaging_enabled_by_member &&
      member.messaging_enabled_by_admin &&
      currentCommunityMember?.messaging_enabled_by_member &&
      currentCommunityMember?.messaging_enabled_by_admin
    );
  };

  return { shouldShowMessageOption };
};
